import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Chip,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import debounce from "lodash/debounce";
import { useSearchQuery } from "../hooks/useSearchQuery";
import { SearchableEntity } from "../services/searchService";
import { commonTextFieldProps } from "../utils/styles";
import CloseIcon from "@mui/icons-material/Close";
interface SearchableAutocompleteProps<T> {
  entityType: SearchableEntity;
  value: T[] | string[] | null;
  onSelect: (option: T | T[] | null) => void;
  onChange: (value: string) => void;
  label: string;
  placeholder?: string;
  getOptionLabel: (option: T | string) => string;
  renderOptionContent?: (option: T) => React.ReactNode;
  renderTags?: (
    value: readonly (T | string)[],
    getTagProps: any
  ) => React.ReactNode;
  noOptionsText?: string;
  disabled?: boolean;
  size?: "small" | "medium";
}

const MultiSearchableAutocomplete = <T extends Record<string, any>>({
  entityType,
  value,
  onSelect,
  onChange,
  label,
  placeholder,
  getOptionLabel,
  renderOptionContent,
  renderTags,
  noOptionsText = "No options found",
  disabled = false,
  size = "small",
}: SearchableAutocompleteProps<T>) => {
  const [searchTerm, setSearchTerm] = useState("");
  const { data: options = [], isLoading: loading } = useSearchQuery(
    entityType as SearchableEntity,
    searchTerm
  );

  const debouncedSearch = useCallback(
    debounce((term: string) => setSearchTerm(term), 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const defaultRenderOptionContent = (option: T) => (
    <>
      <Typography variant="body1" sx={{ fontSize: "0.75rem" }}>
        {option?.name ||
          option?.petrolPumpName ||
          option?.partyName ||
          option?.clientName}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: "0.75rem" }}
      >
        {option.location && `${option.location} -${" "}`}
        {option.contactNumber ||
          option.phoneNumber ||
          option.primaryContactNumber}
      </Typography>
    </>
  );

  return (
    <Autocomplete
      freeSolo
      multiple={true}
      options={options}
      getOptionLabel={getOptionLabel}
      loading={loading}
      renderTags={
        renderTags ||
        ((value: readonly (T | string)[], getTagProps) =>
          value.length > 0 ? (
            <span style={{ fontSize: "0.65rem", color: "#666" }}>
              {`${value.length} item${value.length !== 1 ? "s" : ""} selected`}
            </span>
          ) : null)
      }
      onChange={(_, newValue) => {
        if (Array.isArray(newValue)) {
          // Handle multiple selection
          onSelect(newValue as T[]);
        } else if (typeof newValue === "string") {
          onChange(newValue);
          debouncedSearch(newValue);
        } else if (newValue) {
          onSelect(newValue as T);
        } else {
          onSelect(null);
        }
      }}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === "input") {
          onChange(newInputValue);
          debouncedSearch(newInputValue);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label={label}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={15} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          InputLabelProps={{
            ...params.InputLabelProps,
            shrink:
              (value && Array.isArray(value) && value.length > 0) || undefined,
            sx: { fontSize: "0.65rem" },
          }}
          placeholder={placeholder}
          sx={{
            ...commonTextFieldProps.sx,
            fontSize: "0.65rem",
            "& .MuiInputLabel-root": { fontSize: "0.65rem" },
          }}
          size={size}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        return (
          <li key={key} {...rest}>
            <Box fontSize={12}>
              {renderOptionContent
                ? renderOptionContent(option as T)
                : defaultRenderOptionContent(option as T)}
            </Box>
          </li>
        );
      }}
      noOptionsText={noOptionsText}
      disabled={disabled}
      {...commonTextFieldProps}
    />
  );
};

export default MultiSearchableAutocomplete;
